// volca-top.js — GSAP + ScrollTrigger for VOLCA top page
// Requires: gsap.min.js + ScrollTrigger.min.js loaded before this file

(function () {
  'use strict';

  // hash が無ければ先頭から始める（hash があれば initNavLinks が対象へジャンプ）。
  // 位置復元の無効化そのものは theme.liquid の head 同期 script 側（defer では間に合わない）。
  if (!location.hash) window.scrollTo(0, 0);

  if (typeof gsap === 'undefined') {
    console.warn('[volca-top] GSAP not found — animations disabled');
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  // MENU からのプログラム的ジャンプ中だけ true。ジャンプ時に飛ぶ 'volca:veil-opaque' が
  // Quality 背景を is-revealing 化すると高さが 100vh 潰れ、以降の着地座標がズレるため抑止する。
  // navToken は連打時に「最後のジャンプ」だけが解除するためのガード。
  let navLock = false;
  let navToken = 0;

  // ---- Fade-in animations ----
  // Usage: data-volca-fade="up|left|right|scale"
  //        data-volca-delay="0.2"   (seconds)
  //        data-volca-duration="1"  (seconds)
  function initFadeIns() {
    document.querySelectorAll('[data-volca-fade]').forEach((el) => {
      const direction = el.dataset.volcaFade || 'up';
      const delay = parseFloat(el.dataset.volcaDelay) || 0;
      const duration = parseFloat(el.dataset.volcaDuration) || 1;

      const from = { opacity: 0 };
      const to = { opacity: 1 };
      if (direction === 'up' || direction === '') {
        from.y = 40;
        to.y = 0;
      } else if (direction === 'left') {
        from.x = -40;
        to.x = 0;
      } else if (direction === 'right') {
        from.x = 40;
        to.x = 0;
      } else if (direction === 'scale') {
        from.scale = 0.95;
        to.scale = 1;
      }

      gsap.fromTo(el, from, {
        ...to,
        duration,
        delay,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 82%',
          toggleActions: 'play none none none',
        },
      });
    });
  }

  // ---- Header state (landing ↔ scrolled) ----
  // Switches when the VOLCA logo in the KV section scrolls past the header
  function initHeader() {
    const header = document.getElementById('volca-header');
    const kvLogo = document.querySelector('.volca-kv__logo--volca');
    if (!header || !kvLogo) return;

    ScrollTrigger.create({
      trigger: kvLogo,
      start: 'bottom 80px', // logo bottom hits header bottom (~80px from top)
      onLeave: () => header.classList.add('is-scrolled'),
      onEnterBack: () => header.classList.remove('is-scrolled'),
    });
  }

  // ---- KV logos: fixed のまま、スクロール開始で opacity だけ落とす ----
  function initKvLogos() {
    const logos = document.querySelector('.volca-kv__logos');
    if (!logos) return;

    ScrollTrigger.create({
      trigger: '#kv',
      start: 'top -8%', // KV を少しスクロールしたら
      onEnter: () => gsap.to(logos, { autoAlpha: 0, duration: 0.4, ease: 'power2.in', overwrite: 'auto' }),
      onLeaveBack: () => gsap.to(logos, { autoAlpha: 1, duration: 0.4, ease: 'power2.out', overwrite: 'auto' }),
    });
  }

  // ---- Scroll indicator: Product 直前まで固定表示、Product top が中央でスッと消す ----
  function initScrollIndicator() {
    const scroll = document.querySelector('.volca-kv__scroll');
    if (!scroll) return;

    // KV セクション（z-index:1）の stacking context に閉じ込められないよう body 直下へ移動。
    // CSS で position:fixed + z-index:500 にしてあるので全区間で最前面に出る。
    document.body.appendChild(scroll);

    const product = document.getElementById('product');
    if (!product) return;

    ScrollTrigger.create({
      trigger: product,
      start: 'top 50%', // Product の top が viewport 中央に来たら
      onEnter: () => gsap.to(scroll, { opacity: 0, duration: 0.5, ease: 'power2.out', overwrite: 'auto' }),
      onLeaveBack: () => gsap.to(scroll, { opacity: 1, duration: 0.5, ease: 'power2.out', overwrite: 'auto' }),
    });
  }

  // ---- Concept: テキストを fixed で画面中央に固定し、opacity だけで出し入れ ----
  // 3 枚が中央に重なり、各パネル区間で順に切り替わる。位置は動かさない。
  // 祖先（.volca-section）の overflow は Concept だけ visible。iOS で fixed が下切れするため。
  function initConcept() {
    const section = document.querySelector('.volca-concept');
    if (!section) return;

    // ── Panel 1（タイトル）: fixed 中央固定。scrub でフェードイン→ホールド→アウト ──
    const titlePanel = section.querySelector('.volca-concept__panel--title');
    const titleText = titlePanel && titlePanel.querySelector('.volca-concept__text');
    if (titlePanel && titleText) {
      gsap.set(titleText, { opacity: 0 });
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: titlePanel,
          start: 'top 50%',
          end: 'bottom bottom-=40%',
          scrub: 0.3,
        },
      });
      tl.to(titleText, { opacity: 1, duration: 0.18, ease: 'none' }) // 中央で止まった瞬間フェードイン
        .to(titleText, { opacity: 1, duration: 0.68, ease: 'none' }) // 中央でホールド
        .to(titleText, { opacity: 0, duration: 0.18, ease: 'none' }); // 中央のままフェードアウト

      // section の top が画面 bottom より上にある間、タイトルテキストに .isActive を付けて fixed に。
      ScrollTrigger.create({
        trigger: section,
        start: 'top bottom',
        onEnter: () => titleText.classList.add('isActive'),
        onLeaveBack: () => titleText.classList.remove('isActive'),
      });
    }

    // ── Panel 2（本文）: 画面に入った瞬間に一度だけ fade in。以降は通常フロー ──
    const bodyText = section.querySelector('.volca-concept__text--flow');
    if (bodyText) {
      gsap.set(bodyText, { opacity: 0, y: 30 });
      ScrollTrigger.create({
        trigger: bodyText,
        start: 'top 85%',
        onEnter: () => gsap.to(bodyText, { opacity: 1, y: 0, duration: 1.2, ease: 'power2.out', overwrite: 'auto' }),
        onLeaveBack: () =>
          gsap.to(bodyText, { opacity: 0, y: 30, duration: 0.5, ease: 'power2.in', overwrite: 'auto' }),
      });
    }
  }

  // ---- Quality: 背景スライドショー ----
  function initQuality() {
    const section = document.getElementById('quality');
    if (!section) return;

    const bgImages = Array.from(section.querySelectorAll('.volca-quality__bg-img'));


    // ── 背景スライドショー: 3 秒おきに自動クロスフェード（スクロール非連動）──
    // 画面内にある間だけタイマーを回す。prefers-reduced-motion では送らない。
    if (bgImages.length > 1) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let idx = 0;
      let timer = null;
      const advance = () => {
        idx = (idx + 1) % bgImages.length;
        bgImages.forEach((img, j) => img.classList.toggle('is-active', j === idx));
      };
      const start = () => {
        if (reduce || timer) return;
        timer = setInterval(advance, 3000);
      };
      const stop = () => {
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      };
      ScrollTrigger.create({
        trigger: section,
        start: 'top bottom',
        end: 'bottom top',
        onToggle: (self) => (self.isActive ? start() : stop()),
      });
    }
  }

  // ---- セクションラベル＋イントロ本文 ----
  // Discovery / Story: カーテンが上がりきった瞬間に fixed のラベル・本文を出し、セクションを
  //   抜けるとき（fixed は自分で消さないと残る）に fade out する。
  // Vision: スナップ着地（scrollend）で label → text の順。Quality ラベルは initQuality 側。
  function initLabelReveal() {
    // ラベル: opacity のみ
    const fadeIn = (label) => gsap.to(label, { opacity: 1, duration: 0.9, ease: 'power2.out', overwrite: 'auto' });
    const fadeOut = (label) => gsap.to(label, { opacity: 0, duration: 0.3, ease: 'power2.in', overwrite: 'auto' });
    // 本文: opacity ＋ 下から 30px 上がる（Quality 本文と同じ）
    const textIn = (el) => gsap.to(el, { opacity: 1, duration: 0.8, ease: 'power2.out', overwrite: 'auto' });
    const textOutReset = (el) => gsap.to(el, { opacity: 0, duration: 0.5, ease: 'power2.in', overwrite: 'auto' });
    const textOutHold = (el) => gsap.to(el, { opacity: 0, duration: 0.2, ease: 'power2.in', overwrite: 'auto' });

    // ── Discovery ラベル＋本文 ──
    // ≥750px: 背景が先、ラベル/本文はセクション位置基準で少し遅らせる。
    // <750px: 通常フロー。要素が画面に入った瞬間にフェードイン。
    const discLabel = document.querySelector('.volca-discovery-intro__label');
    const discText = document.querySelector('.volca-discovery-intro__content');
    const discovery = document.getElementById('discovery');
    if (discLabel && discText && discovery) {
      gsap.set([discLabel, discText], { opacity: 0 });

      const discMM = gsap.matchMedia();
      // Desktop: 固定配置なのでセクション位置基準（従来どおり）
      discMM.add('(min-width: 750px)', () => {
        ScrollTrigger.create({
          trigger: discovery,
          start: 'top top-=150',
          onEnter: () => fadeIn(discLabel),
          onLeaveBack: () => fadeOut(discLabel),
        });
        ScrollTrigger.create({
          trigger: discovery,
          start: 'bottom 20%',
          onEnter: () => fadeOut(discLabel),
          onLeaveBack: () => fadeIn(discLabel),
        });
        ScrollTrigger.create({
          trigger: discovery,
          start: 'top top-=150',
          onEnter: () => textIn(discText),
          onLeaveBack: () => textOutReset(discText),
        });
        ScrollTrigger.create({
          trigger: discovery,
          start: 'bottom 65%',
          onEnter: () => textOutHold(discText),
          onLeaveBack: () => textIn(discText),
        });
      });
      // SP: 「背景 → タイトル → 本文」を 0.1s 刻みのスタッガーで出す（トリガ=タイトルが画面下端）。
      discMM.add('(max-width: 749px)', () => {
        const introBg = document.querySelector('.volca-discovery-intro__bg');
        ScrollTrigger.create({
          trigger: discLabel,
          start: 'top bottom',
          onEnter: () => {
            if (introBg) introBg.classList.add('is-visible'); // 1) 背景
            gsap.to(discLabel, { opacity: 1, duration: 0.5, delay: 0.1, ease: 'power2.out', overwrite: 'auto' }); // 2) タイトル
            gsap.to(discText, { opacity: 1, duration: 0.5, delay: 0.2, ease: 'power2.out', overwrite: 'auto' }); // 3) 本文
          },
          onLeaveBack: () => {
            if (introBg) introBg.classList.remove('is-visible');
            gsap.to([discLabel, discText], { opacity: 0, duration: 0.3, ease: 'power2.in', overwrite: 'auto' });
          },
        });
      });
    }

    // ── Story ラベル＋リード本文＋タイトルパネル背景 ──
    // ≥750px: ラベルは fixed で滞留（白背景では黒へ）。背景は白コンテンツが覆うまで画面固定。
    // <750px: 通常フロー。Discovery と同じ 0.1s 刻みのスタッガー。
    const storyLabel = document.getElementById('story-label');
    const story = document.getElementById('story');
    const vision = document.getElementById('vision');
    const storyBg = document.querySelector('.volca-story__bg');
    const storyContent = document.querySelector('.volca-story__content');
    const storyPanel = document.querySelector('.volca-story__title-panel');
    const storyText = document.querySelector('.volca-story__lead-text');
    if (storyLabel && story && storyPanel && storyText && storyContent) {
      const storyMM = gsap.matchMedia();

      // ── Desktop(≥750px): 従来どおりの fixed 演出 ──
      storyMM.add('(min-width: 750px)', () => {
        gsap.set(storyLabel, { opacity: 0 });
        gsap.set(storyText, { opacity: 0, y: 30 });

        // ラベル: セクション先頭で表示 → Vision 接近で消す
        ScrollTrigger.create({
          trigger: story,
          start: 'top 5%',
          onEnter: () => fadeIn(storyLabel),
          onLeaveBack: () => fadeOut(storyLabel),
        });
        ScrollTrigger.create({
          // Vision の上端が画面下20% まで上がった時点で消す（vision に入る手前で消えきる）
          trigger: vision || story,
          start: vision ? 'top 80%' : 'bottom 40%',
          onEnter: () => fadeOut(storyLabel),
          onLeaveBack: () => fadeIn(storyLabel),
        });

        // 背景: 白コンテンツが覆うまで is-revealing（fixed）で画面固定＝ QUALITY→STORY の待機側。
        // 200px 早入りは、absolute→fixed の切替が必ずカーテンの裏で起きるようにするため
        // （ツールバーの伸縮で境界が最大 100px ずれても覆われている）。
        ScrollTrigger.create({
          trigger: story,
          start: 'top bottom+=200',
          endTrigger: storyContent,
          end: 'top top',
          onToggle: (self) => storyBg && storyBg.classList.toggle('is-revealing', self.isActive),
        });

        // 色切替: 白コンテンツ top が fixed ラベルの中央を越えたら黒文字に。
        // ラベル位置はレスポンシブで変わるので getBoundingClientRect から動的算出。
        ScrollTrigger.create({
          trigger: storyContent,
          start: () => {
            const r = storyLabel.getBoundingClientRect();
            return 'top ' + (r.top + r.height / 2) + 'px';
          },
          invalidateOnRefresh: true,
          onEnter: () => storyLabel.classList.add('is-on-light'),
          onLeaveBack: () => storyLabel.classList.remove('is-on-light'),
        });

        // 本文: タイトルパネル停止で下から 30px 上がりながら fade in → 白コンテンツ接近でその場フェードアウト
        const inStoryText = () =>
          gsap.to(storyText, { opacity: 1, y: 0, duration: 1.4, ease: 'power2.out', overwrite: 'auto' });
        const outStoryText = () =>
          gsap.to(storyText, { opacity: 0, duration: 0.6, ease: 'power2.in', overwrite: 'auto' });
        ScrollTrigger.create({
          trigger: storyPanel,
          start: 'top 5%',
          onEnter: inStoryText,
          onLeaveBack: outStoryText,
        });
        ScrollTrigger.create({
          trigger: storyContent,
          start: 'top bottom-=50px',
          onEnter: outStoryText,
          onLeaveBack: inStoryText,
        });

        return () => storyLabel.classList.remove('is-on-light');
      });

      // ── SP(<750px): 通常フロー。背景 → タイトル → 本文 を 0.1s 刻みでスタッガー表示 ──
      storyMM.add('(max-width: 749px)', () => {
        gsap.set([storyLabel, storyText], { opacity: 0, y: 0 });
        if (storyBg) gsap.set(storyBg, { opacity: 0 });

        // ── QUALITY→STORY カーテンアップ（SP の待機側）──
        // ゾーン中だけ story に .is-waiting を付け、背景を fixed で停止させる（CSS・<768px）。
        // opacity は即出す（スタッガーの発火はゾーン中盤＝リビール開始に間に合わない）。
        // ラベル・本文は伏せておき、到着（story top top）でその場にフェードイン。
        // 200px 早入りは切替がカーテンの裏で起きるようにするため。
        ScrollTrigger.create({
          trigger: story,
          start: 'top bottom+=200',
          end: 'top top',
          onToggle: (self) => {
            story.classList.toggle('is-waiting', self.isActive);
            if (self.isActive) {
              if (storyBg) gsap.set(storyBg, { opacity: 1, overwrite: 'auto' }); // 待機背景（カーテンの裏で即出し）
              gsap.to([storyLabel, storyText], { opacity: 0, duration: 0.2, ease: 'power2.in', overwrite: 'auto' });
            } else if (self.direction === 1) {
              // 下向きに抜けた＝ STORY 到着。停止した背景の上に文字をその場でスタッガー表示
              gsap.to(storyLabel, { opacity: 1, duration: 0.5, ease: 'power2.out', overwrite: 'auto' });
              gsap.to(storyText, { opacity: 1, duration: 0.5, delay: 0.1, ease: 'power2.out', overwrite: 'auto' });
            }
            // 上向きに抜けた場合は下のスタッガーの onLeaveBack が背景・文字とも伏せる
          },
        });

        ScrollTrigger.create({
          trigger: storyLabel,
          start: 'top bottom',
          onEnter: () => {
            if (storyBg) gsap.to(storyBg, { opacity: 1, duration: 0.5, ease: 'power2.out', overwrite: 'auto' }); // 1) 背景
            // カーテンアップ中は文字を出さない（到着時に上のゾーントリガがフェードインさせる）
            if (story.classList.contains('is-waiting')) return;
            gsap.to(storyLabel, { opacity: 1, duration: 0.5, delay: 0.1, ease: 'power2.out', overwrite: 'auto' }); // 2) タイトル
            gsap.to(storyText, { opacity: 1, duration: 0.5, delay: 0.2, ease: 'power2.out', overwrite: 'auto' }); // 3) 本文
          },
          onLeaveBack: () => {
            if (storyBg) gsap.to(storyBg, { opacity: 0, duration: 0.3, ease: 'power2.in', overwrite: 'auto' });
            gsap.to([storyLabel, storyText], { opacity: 0, duration: 0.3, ease: 'power2.in', overwrite: 'auto' });
          },
        });

        // タイトルパネルは sticky で画面に貼り付き、白コンテンツがその上へかぶさる。
        // 覆われる途中でラベル・本文が横一線に切られないよう、白が届く手前でフェードアウトする。
        // 発火位置はパネル上端から本文下端までの距離で動的に出す（refresh 時の位置に依存しない）。
        const storyTextGuard = 80; // 本文下端から白までの余白（速いスクロールでもフェードが間に合う距離）
        ScrollTrigger.create({
          trigger: storyContent,
          start: () => {
            const panelTop = storyPanel.getBoundingClientRect().top;
            const textBottom = storyText.getBoundingClientRect().bottom;
            return 'top ' + (textBottom - panelTop + storyTextGuard) + 'px';
          },
          invalidateOnRefresh: true,
          onEnter: () =>
            gsap.to([storyLabel, storyText], { opacity: 0, duration: 0.4, ease: 'power2.in', overwrite: 'auto' }),
          onLeaveBack: () =>
            gsap.to([storyLabel, storyText], { opacity: 1, duration: 0.4, ease: 'power2.out', overwrite: 'auto' }),
        });
      });
    }

    // ── Vision ラベル ──
    // IN: 本文の fade in（top 30%）より先に出すため top 35%（% はスクロールで 100→0 と減る）。
    const visionLabel = document.getElementById('vision-label');
    if (visionLabel && vision) {
      gsap.set(visionLabel, { opacity: 0 });
      ScrollTrigger.create({
        trigger: vision,
        start: 'top 35%',
        onEnter: () => fadeIn(visionLabel),
        onLeaveBack: () => fadeOut(visionLabel),
      });
      ScrollTrigger.create({
        trigger: vision,
        start: 'bottom 40%',
        onEnter: () => fadeOut(visionLabel),
        onLeaveBack: () => fadeIn(visionLabel),
      });
    }

    // ── Quality ラベル（≥750px のみ）: fixed で滞留させ、Vision 手前で消す ──
    // <750px は CSS で absolute（常時表示）なので触らない。
    const qualityLabel = document.getElementById('quality-label');
    const qualitySection = document.getElementById('quality');
    if (qualityLabel && qualitySection) {
      gsap.matchMedia().add('(min-width: 750px)', () => {
        gsap.set(qualityLabel, { opacity: 0 });
        ScrollTrigger.create({
          trigger: qualitySection,
          start: 'top top',
          onEnter: () => fadeIn(qualityLabel),
          onLeaveBack: () => fadeOut(qualityLabel),
        });
        ScrollTrigger.create({
          trigger: qualitySection,
          start: 'bottom 60%', // 消えるタイミングを少し早める（bottom 40% → 60%）
          onEnter: () => fadeOut(qualityLabel),
          onLeaveBack: () => fadeIn(qualityLabel),
        });
      });
    }

    // Vision の本文: section top が画面下 30% に来た瞬間に opacity で表示（label は上のトリガ担当）。
    const visionContent = document.querySelector('.volca-vision__content');
    if (visionContent && vision) {
      gsap.set(visionContent, { opacity: 0 });
      ScrollTrigger.create({
        trigger: vision,
        start: 'top 30%',
        onEnter: () => gsap.to(visionContent, { opacity: 1, duration: 0.2, ease: 'power2.out', overwrite: 'auto' }),
        onLeaveBack: () => gsap.to(visionContent, { opacity: 0, duration: 0.2, ease: 'power2.in', overwrite: 'auto' }),
      });
    }
  }

  // ---- Discovery イントロ背景: bg-discovery.jpg を intro 区間だけ fixed で表示 ----
  // nav の静止区間の中央で is-visible を外し、disc1-scene（recycled 内の sticky）へ引き継ぐ。
  function initDiscoveryIntroBg() {
    const bg = document.querySelector('.volca-discovery-intro__bg');
    const intro = document.getElementById('discovery');
    const nav = document.getElementById('discovery-nav');
    if (!bg || !intro) return;
    // ── nav→RECYCLED のカーテンアップと、写真→Scene1 の引き継ぎ ──
    // nav は 2 画面の高さで、カード面（__inner）は sticky の 1 画面（CSS 参照）。
    //   (1) カードがせり上がって写真を覆う →(2) 1 画面ぶん静止 →(3) 押し出されて上へ抜ける
    // 写真を消すのは (2) の中央（'center center'）。前後に半画面ずつ余裕があるので、
    // innerHeight の変動（最大 100px）や onToggle の 1 フレーム遅れでもズレない。
    // 写真を JS で追従スライドさせてはいけない（scroll イベント後の JS は構造的に遅れる）。
    ScrollTrigger.create({
      trigger: intro,
      start: 'top top', // intro が画面いっぱいに来たら表示（＝Concept 本文が画面外に出きってから）
      endTrigger: nav || intro,
      end: nav ? 'center center' : 'bottom top', // nav 静止区間の中央で消す
      onToggle: (self) => bg.classList.toggle('is-visible', self.isActive),
    });
  }

  // ---- KV の fixed 動画: 見えている区間だけ表示＋再生する ----
  // 使うのは KV / Concept と VISION（VISION は背景 transparent で透かす）。間の Discovery /
  // Quality / Story と Product は各背景が覆いきるので伏せる（デコードも止まる）。
  // 消し方（フェードさせず時間で切る）の理由は volca-kv.liquid の .is-covered を参照。
  function initKvVideoVisibility() {
    const covers = document.querySelectorAll('.volca-kv__bg, .volca-kv__overlay');
    const intro = document.getElementById('discovery');
    const vision = document.getElementById('vision');
    if (!covers.length || !intro) return;
    const video = document.querySelector('.volca-kv__bg video');

    // 2 つの区間の状態を持ち合わせて「隠すかどうか」を決める。
    // ScrollTrigger を 2 本立てて同じクラスを直接触ると、後から発火した方が勝って取り違える。
    let afterConcept = false; // Discovery intro 以降にいる
    let inVision = false; // VISION が画面にかかっている
    let pauseTimer = null;

    const apply = () => {
      const hidden = afterConcept && !inVision;
      covers.forEach((el) => el.classList.toggle('is-covered', hidden));
      if (!video) return;
      clearTimeout(pauseTimer);
      if (hidden) {
        // 止めるのは消えきってから（CSS の 0.65s より後）。先に止めると静止画が一瞬見える。
        pauseTimer = setTimeout(() => video.pause(), 700);
      } else {
        // 復帰は即座に。autoplay ポリシーの拒否は握りつぶす（muted なので通常は通る）。
        const played = video.play();
        if (played && played.catch) played.catch(() => {});
      }
    };

    ScrollTrigger.create({
      trigger: intro,
      start: 'top top',
      end: 'max',
      onToggle: (self) => {
        afterConcept = self.isActive;
        apply();
      },
    });

    if (vision) {
      ScrollTrigger.create({
        trigger: vision,
        start: 'top bottom', // 画面下端に入った時点で ON（まだ Story が覆っているので切替は見えない）
        end: 'bottom top', // 画面上端を抜けたら OFF（Product が全面を覆っている）
        onToggle: (self) => {
          inVision = self.isActive;
          apply();
        },
      });
    }
  }

  // ---- Discovery Scene 1: recycled 内の sticky 背景アニメ ----
  function initDiscoveryScene1() {
    const scene = document.querySelector('.disc1-scene');
    const shade = document.querySelector('.disc1-shade');
    const intro = document.getElementById('discovery');
    const recycled = document.getElementById('volca-discovery-recycled');
    const recycledContents = recycled && recycled.querySelector('.volca-discovery__contents');
    if (!scene || !shade || !intro || !recycled || !recycledContents) return;

    // scene 表示範囲: intro が画面いっぱいに来た瞬間〜 recycled 下端が viewport 上端を抜けるまで。
    // 早めに付けるのは、nav の静止区間で写真→Scene1 の引き継ぎが起きる前に opacity 遷移を終えるため。
    ScrollTrigger.create({
      trigger: intro,
      start: 'top top',
      endTrigger: recycled,
      end: 'bottom top',
      onToggle: (self) => scene.classList.toggle('is-visible', self.isActive),
    });

    // シェード: intro が画面いっぱいで表示、__contents top 80% で 0.4s フェード。
    ScrollTrigger.create({
      trigger: intro,
      start: 'top top',
      endTrigger: recycledContents,
      end: 'top 80%',
      onToggle: (self) => shade.classList.toggle('is-visible', self.isActive),
    });

    // アニメ再生: __contents が画面の 80% に入ったら開始、戻ったら停止
    ScrollTrigger.create({
      trigger: recycledContents,
      start: 'top 80%',
      endTrigger: recycled,
      end: 'bottom top',
      onToggle: (self) => scene.classList.toggle('is-playing', self.isActive),
    });

    // ── Scene1→Scene2（RECYCLED→CORE）のカーテンアップ（カーテン側）──
    // scene は recycled 先頭の sticky（CSS 参照）。recycled の最後の 1 画面でネイティブに
    // 押し出されて上へ抜ける＝それがカーテン。裏では Scene2(CORE) が is-prefixed で停止している。
    // recycled 自体が z:9 ＝ scene は常に core(z:3) より前面なので z の調停は不要。

    // ── nav→Scene1 のカーテンアップ（背面ステイ側）──
    // sticky は recycled 上端が viewport 上端に達するまで効かず、それ以前は flow でせり上がる。
    // そこで手前（top bottom 〜 top top）だけ fixed で停止させ、カーテンが抜けた先に現す。
    // top top で外すと sticky(top:0) と同座標なのでシームレス。200px 早入りは切替を裏で起こすため。
    ScrollTrigger.create({
      trigger: recycled,
      start: 'top bottom+=200',
      end: 'top top',
      onToggle: (self) => scene.classList.toggle('is-prefixed', self.isActive),
    });
  }

  // ---- Discovery Scene 2: core 範囲で fixed 背景アニメ + カメラパン + マグマ柱 reveal ----
  function initDiscoveryScene2() {
    const scene = document.querySelector('.disc2-scene');
    const shade = document.querySelector('.disc2-shade');
    const stage = scene && scene.querySelector('.disc2-stage');
    const magmaStack = scene && scene.querySelector('.disc2-magma-stack');
    const core = document.getElementById('volca-discovery-core');
    const coreTitle = core && core.querySelector('.volca-discovery__title');
    const coreContents = core && core.querySelector('.volca-discovery__contents');
    if (!scene || !shade || !stage || !magmaStack || !core || !coreTitle || !coreContents) return;

    // stage は CSS で bottom: 0 + left: 50% に置く。xPercent: -50 だけ JS で当てる
    gsap.set(stage, { xPercent: -50 });

    // scene の画面貼り付きと CORE→QUALITY のカーテンは CSS の sticky が担う（.disc2-scene）。
    // GSAP pin を使ってはいけない: ツールバー伸縮で refresh が走ると、is-prefixed（fixed）中の
    // 位置を自然位置と誤測定して -1 画面のオフセットを焼き込み、カーテンが消える。

    // ── RECYCLED→CORE のカーテンアップ（背面ステイ側）──
    // Scene1 と同型: 手前（core top bottom 〜 top top）だけ fixed で停止させ、カーテン
    // （Scene1 の sticky 押し出し）が抜けた先に現す。top top で外せば sticky と同座標。
    // 200px 早入りは切替が Scene1 に覆われた裏で起きるようにするため。
    ScrollTrigger.create({
      trigger: core,
      start: 'top bottom+=200',
      end: 'top top',
      onToggle: (self) => scene.classList.toggle('is-prefixed', self.isActive),
    });

    // ── CORE→QUALITY のカーテンアップ ──
    // カーテン自体は sticky のネイティブ押し出し。JS はクラスの付け外しだけ:
    //  1) core .is-curtain-out (z:5): disc2 を quality(z:4) より前面にする
    //  2) quality .is-waiting: 背景ステージを fixed で待機（container の -100vh 解除も同時。
    //     外すと sticky(top:0) と同座標なので本文位置は動かない）
    // 200px 早入りは切替をカーテンの裏で起こすため。状態は単一トリガの isActive から一意に決める。
    const quality = document.getElementById('quality');
    if (quality) {
      ScrollTrigger.create({
        trigger: quality,
        start: 'top bottom+=200',
        end: 'top top',
        onToggle: (self) => {
          core.classList.toggle('is-curtain-out', self.isActive);
          quality.classList.toggle('is-waiting', self.isActive);
        },
      });
    }

    // シェード: title 表示中は暗く、contents top が viewport 80% に来たらフェードアウト
    ScrollTrigger.create({
      trigger: core,
      start: 'top bottom',
      endTrigger: coreContents,
      end: 'top 80%',
      onToggle: (self) => shade.classList.toggle('is-visible', self.isActive),
    });

    // scene は core 内の sticky なので core タイトルと同期して動く。JS のスライド制御は不要。

    // カメラパン（scrub）: contents が画面下端〜上端に上がる間で stage を下へシフト（地下→山頂）。
    // panFloor はマグマ reveal 完了時点の y を床にして、戻り方向はそれ以上下がらない。
    // 基準は innerHeight ではなく scene の高さ(100lvh)。innerHeight 基準だとツールバー分だけ
    // stage が下へ流れ、fixed の FLOW ボタンだけ取り残されて地形からズレる。
    const panEnd = () => stage.offsetHeight - scene.clientHeight;
    let panFloor = 0;
    gsap.fromTo(
      stage,
      { y: 0 },
      {
        y: panEnd,
        ease: 'none',
        scrollTrigger: {
          trigger: coreContents,
          start: 'top bottom',
          end: 'top top',
          scrub: true,
          invalidateOnRefresh: true,
        },
      },
    );
    gsap.ticker.add(() => {
      if (panFloor <= 0) return;
      const y = gsap.getProperty(stage, 'y');
      if (y < panFloor) gsap.set(stage, { y: panFloor });
    });

    // マグマ柱 reveal: contents が 10% 見えた瞬間に auto-play（スクロール非連動）
    // 完了したら scene に is-playing を付与 → smoke/splash がそこから動き始める
    // 同時に panFloor を現在 y で固定 → 以降スクロールで戻っても地下に戻らない
    gsap.set(magmaStack, { clipPath: 'inset(100% 0 0 0)' });
    ScrollTrigger.create({
      trigger: coreContents,
      start: 'top 90%',
      once: true,
      onEnter: () => {
        gsap.to(magmaStack, {
          clipPath: 'inset(0% 0 0 0)',
          duration: 2.5,
          ease: 'power2.out',
          onComplete: () => {
            scene.classList.add('is-playing');
            panFloor = gsap.getProperty(stage, 'y');
          },
        });
      },
    });
  }

  // scroll-snap を ON にするときの値。reduced-motion と ≤989px は 'none'。
  // タッチの慣性スクロールと mandatory が綱引きして「勝手に飛ぶ／指が効かない」になるため。
  const SNAP_ON =
    window.matchMedia('(prefers-reduced-motion: reduce)').matches || window.matchMedia('(max-width: 989px)').matches
      ? 'none'
      : 'y mandatory';

  // Discovery Nav 専用の「弱い」スナップ値。mandatory ではなく proximity（近くで止まった時だけ緩く吸着）。
  // reduced-motion / ≤989px は snap しない（SNAP_ON と同じ判定＝'none'）。
  const SNAP_NAV = SNAP_ON === 'none' ? 'none' : 'y proximity';

  // Quality は snap 点の無い長文区間。mandatory を効かせると Quality を飛び越して次の snap 点
  // （Story）へ吸着し、本文が一気に駆け抜ける。snap の設定はすべて setSnap を通す。
  const snapRoot = document.documentElement;
  function inQualityView() {
    const q = document.getElementById('quality');
    if (!q) return false;
    const r = q.getBoundingClientRect();
    // 可視全域で禁止する。中央基準だと「入る瞬間」に間に合わず、mandatory のまま飛び越される。
    return r.top < window.innerHeight && r.bottom > 0;
  }
  // Discovery（intro / nav / recycled / core）区間は snap を完全に切る。隣接する mandatory 区間
  // （Story 側）が伸びてきて「勝手にどこかへ吸着」するのを防ぐ。どれか 1 つでも可視なら none。
  const DISCOVERY_IDS = ['discovery', 'discovery-nav', 'volca-discovery-recycled', 'volca-discovery-core'];
  function inDiscoveryView() {
    for (const id of DISCOVERY_IDS) {
      const el = document.getElementById(id);
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) return true;
    }
    return false;
  }
  // Discovery Nav（2 択セクション）が見えているか。見えている間だけ弱い proximity スナップにする。
  function inNavView() {
    const el = document.getElementById('discovery-nav');
    if (!el) return false;
    const r = el.getBoundingClientRect();
    return r.top < window.innerHeight && r.bottom > 0;
  }
  function setSnap(v) {
    // SNAP_ON を要求されても区間ごとに強さを変える:
    //   Quality 可視 → none ／ Discovery 可視 → none（ただし Nav 中だけ proximity）／ 他 → mandatory
    // guard 方式なので、複数トリガが呼んでも常に現在位置で正しい値に収束する。
    if (v === SNAP_ON) {
      if (inQualityView()) {
        snapRoot.style.scrollSnapType = 'none';
        return;
      }
      if (inDiscoveryView()) {
        snapRoot.style.scrollSnapType = inNavView() ? SNAP_NAV : 'none';
        return;
      }
    }
    snapRoot.style.scrollSnapType = v;
  }

  // ---- フローティング購入ボタン: Discovery 以降ずっと表示 ----
  // start: 'top bottom' ＝ Discovery の上端が画面下端に触れた瞬間＝出はじめ。
  // 消えるのは onLeaveBack だけなので、以降 Product まで出っぱなしになる。
  function initBtnProduct() {
    const btn = document.getElementById('volca-btn-product');
    const discovery = document.getElementById('discovery');
    if (!btn || !discovery) return;
    ScrollTrigger.create({
      trigger: discovery,
      start: 'top bottom',
      onEnter: () => btn.classList.add('is-visible'),
      onLeaveBack: () => btn.classList.remove('is-visible'),
    });
  }

  // ---- Quality は snap 点の無い長文セクション＝見えている間は snap を切る ----
  // Quality パネルには scroll-snap-align が無く、mandatory のまま入るとブラウザが Quality を
  // 飛び越して Story へ吸着する。見え始め（top bottom）で none、見え終わり（bottom top）で戻す。
  // ※ CSS の初期値を mandatory にすると、ロード時に最初の snap 点へ強制スナップされる。
  function initSnapHead() {
    const quality = document.getElementById('quality');
    if (!quality) return;
    snapRoot.style.scrollSnapType = 'none'; // 初期 = ロード時は none
    ScrollTrigger.create({
      trigger: quality,
      start: 'top bottom', // Quality 上端がビューポート下端に入った＝見え始め
      end: 'bottom top', // Quality 下端がビューポート上端を抜けた＝見え終わり
      onToggle: (self) => setSnap(self.isActive ? 'none' : SNAP_ON),
    });
  }

  // ---- Product〜Footer は自由スクロールで終わる ----
  // mandatory は末尾にスナップ点が無いと末尾を捕まえ、Product〜Footer に到達できなくなる。
  // Product が入った瞬間に none にし、上（Vision 側）へ戻ると CSS の値へ復帰する。
  function initSnapTail() {
    const product = document.querySelector('.volca-product');
    if (!product) return;
    const root = document.documentElement;
    ScrollTrigger.create({
      trigger: product,
      start: 'top bottom', // Product 上端がビューポート下端に入った瞬間
      onEnter: () => {
        setSnap('none');
      },
      onLeaveBack: () => {
        setSnap(SNAP_ON);
      }, // Quality/Discovery 区間へ戻る
    });
  }

  // ---- RECYCLED→CORE / CORE→STORY のカーテン区間はスナップを切る ----
  // mandatory は操作の節目ごとに直近スナップ点へ吸着しようとするため、スクロール連動の
  // カーテンと喧嘩して「ガタガタ」する。区間を抜けたら SNAP_ON へ戻す。
  function initSnapCurtains() {
    const root = document.documentElement;
    const recycled = document.getElementById('volca-discovery-recycled');
    const story = document.getElementById('story');
    // RECYCLED→CORE: recycled 下端が画面下端→画面上端（= disc1-scene の y スライドと同区間）
    if (recycled) {
      ScrollTrigger.create({
        trigger: recycled,
        start: 'bottom bottom',
        end: 'bottom top',
        onToggle: (self) => {
          setSnap(self.isActive ? 'none' : SNAP_ON);
        },
      });
    }
    // CORE→STORY: story 上端が画面下端→画面上端（= disc2/storyPanel の transform と同区間）
    if (story) {
      ScrollTrigger.create({
        trigger: story,
        start: 'top bottom',
        end: 'top top',
        onToggle: (self) => {
          setSnap(self.isActive ? 'none' : SNAP_ON);
        },
      });
    }
  }

  // ---- Discovery Nav だけ弱い画面スナップ（proximity）----
  // nav は Discovery 内で唯一の scroll-snap-align 要素なので、proximity でも吸着先は nav だけ。
  // ここでは可視境界で setSnap を呼び直すだけ＝実際の判定は setSnap の guard が担う。
  function initSnapNav() {
    const nav = document.getElementById('discovery-nav');
    if (!nav) return;
    ScrollTrigger.create({
      trigger: nav,
      start: 'top bottom', // nav が見え始め
      end: 'bottom top', // nav が見え終わり
      onToggle: () => setSnap(SNAP_ON), // guard が nav 可視中だけ proximity にする
    });
  }

  // ---- Discovery の FLOW ボタン群: fixed（CSS）でその場に表示／その場で fade out ----
  //   ・透明時に他セクションのクリックを奪わないよう autoAlpha（opacity＋visibility）で制御。
  //   ・表示完了後は inline opacity/transform を消して CSS の :hover{opacity:.8} を生かす。
  // ---- 固定要素を「バンドの内側にいる間だけ表示」する共通ヘルパー ----
  // 状態は 2 本のトリガの通過イベントではなく、単一トリガの isActive から一意に決める。
  // 高速スクロールや途中リロードでペアが片方しか発火せず、fixed 要素が別セクションに
  // 取り残されるのを防ぐため。ロード後は resting 状態を現在位置へ一度だけ強制同期する。
  //   h: { showDown, showUp, hideDown, hideUp, setShown, setHidden }
  function bindBandReveal(triggerEl, startStr, endStr, h) {
    const st = ScrollTrigger.create({
      trigger: triggerEl,
      start: startStr,
      end: endStr,
      onToggle: (self) => {
        if (self.isActive) (self.direction === -1 ? h.showUp : h.showDown)();
        else (self.direction === -1 ? h.hideUp : h.hideDown)();
      },
    });
    const onceSync = () => {
      if (st.isActive) h.setShown();
      else h.setHidden();
      ScrollTrigger.removeEventListener('refresh', onceSync);
    };
    ScrollTrigger.addEventListener('refresh', onceSync);
    return st;
  }

  function initDiscoveryFlow() {
    ['--rw', '--cw'].forEach((mod) => {
      const group = document.querySelector('.volca-discovery__btn-modal' + mod);
      if (!group) return;
      const btns = [...group.querySelectorAll('.volca-discovery__flow-btn')];
      if (!btns.length) return;
      const contents = group.closest('.volca-discovery__contents') || group;
      // 【重要】FLOW ボタンの表示/非表示は GSAP の tween ではなく CSS クラス（.is-shown）で駆動する。
      // stagger tween だと、後ろのボタンが出始める前に hide を過ぎた場合に kill しきれず、
      // hide のあとから遅れて fade in して残る（実測で確認）。クラス駆動なら状態が冪等。
      // stagger とフェードは CSS の transition-delay 側（volca-discovery.css）。
      const show = () => group.classList.add('is-shown');
      const hide = () => group.classList.remove('is-shown');
      // 表示区間の終わり。全ブレイクポイント共通で 'bottom 98%'（fixed なので SP も PC と同じ）。
      bindBandReveal(contents, 'top 20%', 'bottom 98%', {
        showDown: show,
        showUp: show,
        hideDown: hide,
        hideUp: hide,
        setShown: show,
        setHidden: hide,
      });
    });
  }

  // ---- Discovery タイトル見出し(RECYCLED/CORE): 画面中央に fixed で固定して滞留 ----
  // IN=パネル top 0、OUT=パネル bottom 65%（Quality イントロ本文と同じタイミング）。
  function initDiscoveryTitles() {
    document.querySelectorAll('.volca-discovery__title').forEach((panel) => {
      const text = panel.querySelector('.volca-discovery__title-inner');
      if (!text) return;
      // 画面中央に固定（中央寄せなので yPercent:-50）。opacity は GSAP が制御。
      gsap.set(text, {
        position: 'fixed',
        top: '50%',
        left: '50%',
        xPercent: -50,
        yPercent: -50,
        opacity: 0,
        zIndex: 10,
      });
      const tIn = () => gsap.to(text, { opacity: 1, duration: 0.8, ease: 'power2.out', overwrite: 'auto' });
      const tOut = () => gsap.to(text, { opacity: 0, duration: 0.2, ease: 'power2.in', overwrite: 'auto' });
      // バンド = パネル top0 〜 bottom65%。中にいる間だけ表示（単一トリガで状態を一意化）
      bindBandReveal(panel, 'top 0', 'bottom 65%', {
        showDown: tIn,
        showUp: tIn,
        hideDown: tOut,
        hideUp: tOut,
        setShown: () => gsap.set(text, { opacity: 1 }),
        setHidden: () => gsap.set(text, { opacity: 0 }),
      });
    });
  }

  // ---- Discovery テキストパネル(RECYCLED/CORE の左下): fixed でその場に表示/非表示 ----
  // 所属する contents が画面に出ている間だけ fade in する。
  function initDiscoveryTextPanels() {
    document.querySelectorAll('.volca-discovery__text-panel').forEach((panel) => {
      const contents = panel.closest('.volca-discovery__contents');
      if (!contents) return;
      gsap.set(panel, { opacity: 0 });
      const show = () => gsap.to(panel, { opacity: 1, duration: 0.8, ease: 'power2.out', overwrite: 'auto' });
      const hide = () => gsap.to(panel, { opacity: 0, duration: 0.2, ease: 'power2.in', overwrite: 'auto' });
      // バンド = contents top30% 〜 bottom98%。98% を大きくするほど早く消える＝次との被りが短い。
      bindBandReveal(contents, 'top 30%', 'bottom 98%', {
        showDown: show,
        showUp: show,
        hideDown: hide,
        hideUp: hide,
        setShown: () => gsap.set(panel, { opacity: 1 }),
        setHidden: () => gsap.set(panel, { opacity: 0 }),
      });
    });
  }

  // Story の各画像を枠より縦に大きく取り、yPercent で ±10%（画像高さ基準）ゆっくり上下させる。
  // CSS で上下 15% ずつの余白を確保済み。prefers-reduced-motion 尊重。
  function initStoryParallax() {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    document.querySelectorAll('.volca-story__img').forEach((wrap) => {
      const img = wrap.querySelector('.volca-story__img-media');
      if (!img) return;
      gsap.fromTo(
        img,
        { yPercent: -10 },
        {
          yPercent: 10,
          ease: 'none',
          scrollTrigger: {
            trigger: wrap,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        },
      );
    });
  }

  // ---- ヘッダー MENU からのナビゲーション ----
  // 標準の anchor jump では scroll-snap ＋ sticky が絡んで ScrollTrigger の onEnter を取りこぼし、
  // 目的セクションの fixed テキストが opacity:0 のまま残る。自前で scroll → イントロを再生する。
  function initNavLinks() {
    // 'story-content' は /pages/story の「TOPに戻る」着地点（STORY intro を飛ばして白コンテンツ本文へ）
    // 'volca-discovery-recycled' / 'volca-discovery-core' は Discovery Nav の 2 択ボタンの遷移先（Scene1/Scene2）
    const TARGETS = new Set([
      'kv',
      'concept',
      'quality',
      'discovery',
      'story',
      'story-content',
      'volca-discovery-recycled',
      'volca-discovery-core',
      'vision',
      'movie',
      'product',
    ]);

    document.querySelectorAll('a[href]').forEach((link) => {
      const href = link.getAttribute('href') || '';
      const m = href.match(/#([\w-]+)$/);
      if (!m) return;
      const id = m[1];
      if (!TARGETS.has(id) || !document.getElementById(id)) return;
      link.addEventListener('click', (e) => {
        e.preventDefault();
        goToSection(id);
      });
    });

    // ロード時に URL hash があれば同じ処理（クロスページ /#xxx 着地に対応）
    if (location.hash) {
      const id = location.hash.slice(1);
      if (TARGETS.has(id) && document.getElementById(id)) {
        // ブラウザの自動 anchor jump は止められないので、layout 確定後に再ジャンプ＋イントロ発火
        requestAnimationFrame(() => goToSection(id));
      }
    }
  }

  // Discovery の固定テキストを確実に伏せる（intro ラベル/本文・見出し・text-panel・FLOW ボタン）。
  // MENU の瞬間ジャンプ（scrollTo instant + refresh）は通過コールバックを取りこぼすため、
  // ScrollTrigger 任せにせず明示的に opacity:0 へ落とす。
  function forceHideDiscovery() {
    const sel = [
      '.volca-discovery-intro__label',
      '.volca-discovery-intro__content',
      '.volca-discovery__title-inner',
      '.volca-discovery__text-panel',
    ].join(',');
    document.querySelectorAll(sel).forEach((el) => {
      gsap.killTweensOf(el);
      gsap.set(el, { opacity: 0 });
    });
    // FLOW ボタン群はクラス駆動（.is-shown）なので外す
    document
      .querySelectorAll('.volca-discovery__btn-modal--rw, .volca-discovery__btn-modal--cw')
      .forEach((g) => g.classList.remove('is-shown'));
  }

  // Quality の fixed ラベル（≥750px）を確実に伏せる。瞬間ジャンプは onLeaveBack を取りこぼし、
  // 着地先のラベルと重なるため。<750px は absolute（フロー）で画面外なので触らない。
  function forceHideQualityLabel() {
    if (!window.matchMedia('(min-width: 750px)').matches) return;
    const label = document.getElementById('quality-label');
    if (label) {
      gsap.killTweensOf(label);
      gsap.set(label, { opacity: 0 });
    }
  }

  // iOS WebKit は瞬間 scrollTo 後、fixed 要素を opacity:1 でも再描画しないことがある（Chrome では
  // 再現しない）。display を同フレーム内で切り替えて強制的に再レイアウトさせる（ちらつきは出ない）。
  function nudgeFixedRepaint() {
    const sel = [
      '#quality-label',
      '.volca-discovery-intro__label',
      '.volca-discovery-intro__content',
      '#story-label',
      '.volca-story__lead-text',
      '#vision-label',
      '.volca-vision__content',
    ].join(',');
    document.querySelectorAll(sel).forEach((el) => {
      const prev = el.style.display;
      el.style.display = 'none';
      void el.offsetHeight; // reflow を強制
      el.style.display = prev;
    });
  }

  function goToSection(id) {
    const target = document.getElementById(id);
    if (!target) return;

    // 別セクションへ飛ぶときは、その先に属さない固定テキストを消す（飛ぶ先は playIntroFor が出す）。
    if (id !== 'discovery') forceHideDiscovery();
    if (id !== 'quality') forceHideQualityLabel();

    // mandatory snap が jump 中に yank するのを避けて一時 off
    const root = document.documentElement;
    const prevSnap = root.style.scrollSnapType;
    root.style.scrollSnapType = 'none';

    // ジャンプ中の veil-opaque で Quality 背景が潰れ、下のセクションの着地座標がズレるのを止める。
    navLock = true;
    const myNav = ++navToken;

    // 【重要】先に ScrollTrigger を再計算してから着地座標を測る。
    // 測ってから refresh すると geometry がずれて着地点が狂う（着地ズレの正体）。
    ScrollTrigger.refresh();

    // 'movie' だけ「BRAND MOVIE ボタンが画面中央」に来るよう center 寄せ。他は section top を画面 top へ。
    const computeY = () => {
      const r = target.getBoundingClientRect();
      return id === 'movie'
        ? Math.round(window.scrollY + r.top + r.height / 2 - window.innerHeight / 2)
        : Math.round(window.scrollY + r.top);
    };
    window.scrollTo({ top: computeY(), behavior: 'instant' });
    if (location.hash !== '#' + id) history.replaceState(null, '', '#' + id);

    // 瞬間ジャンプは onEnter を取りこぼすため、着地後にイントロを明示的に再生する。
    playIntroFor(id);

    // 着地後の保険: reveal の付け外しで上のセクションが reflow して目的地が微ズレした場合に備え、
    // 数フレームだけ位置を監視して補正する（主要因の Quality 潰れは navLock で抑止済み）。
    // 窓の間は snap を none に押さえ続ける: 最初のフレームで snap ハンドラが mandatory を書き戻し、
    // 着地点（特に Quality＝スナップ点でない）から近傍へ吸着して 1 画面ズレるため。
    let frames = 0;
    const settle = () => {
      root.style.scrollSnapType = 'none';
      const y = computeY();
      if (Math.abs(window.scrollY - y) > 1) window.scrollTo({ top: y, behavior: 'instant' });
      if (frames++ < 8) {
        requestAnimationFrame(settle);
      } else {
        setSnap(prevSnap); // Quality 着地時は mandatory を復元しない（駆け抜け防止）
        // 着地確定後にイントロを再アサート＋fixed 要素を強制再描画（iOS の再描画抜け対策）。
        // 着地中の scrollTo 連打で OUT トリガが opacity を戻すケースもここで上書き回復する。
        playIntroFor(id);
        nudgeFixedRepaint();
      }
    };
    requestAnimationFrame(settle);

    // PC の veil tween 完了（~300ms）後に飛ぶ spurious な veil-opaque まで抑止するため、
    // 位置補正の窓より長めに保持してから解除する（連打時は最後のジャンプだけが解除）。
    setTimeout(() => {
      if (navToken === myNav) navLock = false;
    }, 700);
  }

  // 各セクションのイントロ「IN」を直接叩く。重ね打ちでも opacity:1 / y:0 へ収束するだけで害無し。
  function playIntroFor(id) {
    if (id === 'quality') {
      // MENU ジャンプは onEnter を取りこぼしてラベルが opacity:0 のまま残るので明示表示する。
      // ≥750px は fixed で GSAP 制御、<750px は CSS で常時表示。どちらでも可視状態へ収束する。
      const label = document.getElementById('quality-label');
      if (label) gsap.to(label, { opacity: 1, duration: 0.9, ease: 'power2.out', overwrite: 'auto' });
      return;
    }
    if (id === 'discovery') {
      // 着地点は背景トリガーの境界ぴったりで onToggle を取りこぼすので、is-visible を明示的に付ける。
      const bg = document.querySelector('.volca-discovery-intro__bg');
      const label = document.querySelector('.volca-discovery-intro__label');
      const text = document.querySelector('.volca-discovery-intro__content');
      if (bg) bg.classList.add('is-visible');
      if (label) gsap.to(label, { opacity: 1, duration: 0.9, ease: 'power2.out', overwrite: 'auto' });
      if (text) gsap.to(text, { opacity: 1, duration: 0.8, ease: 'power2.out', overwrite: 'auto' });
      return;
    }
    if (id === 'story') {
      const label = document.getElementById('story-label');
      const bg = document.querySelector('.volca-story__bg');
      const lead = document.querySelector('.volca-story__lead-text');
      if (bg) bg.classList.add('is-revealing');
      if (label) gsap.to(label, { opacity: 1, duration: 0.9, ease: 'power2.out', overwrite: 'auto' });
      if (lead) gsap.to(lead, { opacity: 1, y: 0, duration: 1.4, ease: 'power2.out', overwrite: 'auto' });
      return;
    }
    if (id === 'story-content') {
      // STORY 本文へ着地: イントロ演出は出さず、≥750px の fixed ラベルだけ黒(.is-on-light)で表示する
      // （瞬間ジャンプは色切替トリガーを取りこぼす）。<750px はラベルがフロー＝画面外なので触らない。
      const label = document.getElementById('story-label');
      if (label && window.matchMedia('(min-width: 750px)').matches) {
        label.classList.add('is-on-light');
        gsap.to(label, { opacity: 1, duration: 0.9, ease: 'power2.out', overwrite: 'auto' });
      }
      return;
    }
    if (id === 'volca-discovery-recycled' || id === 'volca-discovery-core') {
      // Discovery Nav の 2 択から着地。瞬間ジャンプは title の onEnter を取りこぼして大見出しが
      // 出ないので、着地位置で本来出るべき title-inner を明示表示する（FLOW／text-panel は各自発火）。
      const sec = document.getElementById(id);
      const titleInner = sec && sec.querySelector('.volca-discovery__title-inner');
      if (titleInner) gsap.to(titleInner, { opacity: 1, duration: 0.8, ease: 'power2.out', overwrite: 'auto' });
      return;
    }
    if (id === 'vision' || id === 'movie') {
      // movie は Vision の中央あたりに着地するので、Vision のイントロをそのまま発火
      const label = document.getElementById('vision-label');
      const content = document.querySelector('.volca-vision__content');
      if (label) gsap.to(label, { opacity: 1, duration: 0.9, ease: 'power2.out', overwrite: 'auto' });
      if (content) gsap.to(content, { opacity: 1, duration: 0.4, ease: 'power2.out', overwrite: 'auto' });
      return;
    }
    // kv / concept / product: fixed イントロ無し（自然な scroll fade で十分）
  }

  function init() {
    initHeader();
    initKvLogos();
    initScrollIndicator();
    initConcept();
    initQuality();
    initLabelReveal();
    initDiscoveryIntroBg();
    initKvVideoVisibility();
    initDiscoveryScene1();
    initDiscoveryScene2();
    initDiscoveryTitles();
    initDiscoveryTextPanels();
    initDiscoveryFlow();
    initStoryParallax();
    initFadeIns();
    initBtnProduct();
    initSnapHead();
    initSnapTail();
    initSnapCurtains();
    initSnapNav();
    ScrollTrigger.refresh();
    initNavLinks();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
